import { Task as FhirTask } from 'fhir/r4b';

export interface Task {
  id: string;
  category: string;
  createdDate: string;
  title: string;
  subtitle: string;
  details?: string;
  status: string;
  action?: {
    name: string;
    link: string;
  };
  assignee?: {
    id: string;
    name: string;
    date: string;
  };
  createdById?: string;
  alert?: TaskAlertCode;
  completable: boolean;
  /** Past its `restriction.period.end` and still open (referral tasks waiting on a response). */
  overdue?: boolean;
}
export interface CreateManualTaskRequest {
  category: string;
  appointmentId?: string;
  orderId?: string;
  encounterId?: string;
  taskTitle: string;
  taskDetails?: string;
  assignee?: {
    id: string;
    name: string;
  };
  location: {
    id: string;
    name: string;
  };
  patient?: {
    id: string;
    name: string;
  };
  createdBy?: string;
}

export const MANUAL_TASK = {
  category: {
    externalLab: 'manual-external-lab',
    inHouseLab: 'manual-in-house-lab',
    inHouseMedications: 'manual-in-house-medications',
    nursingOrders: 'manual-nursing-orders',
    patientFollowUp: 'manual-patient-follow-up',
    procedures: 'manual-procedures',
    radiology: 'manual-radiology',
    erx: 'manual-erx',
    charting: 'manual-charting',
    coding: 'manual-coding',
    billing: 'manual-billing',
    other: 'manual-other',
  },
  input: {
    title: 'title',
    details: 'details',
    providerName: 'provider-name',
    appointmentId: 'appointment-id',
    orderId: 'order-id',
    encounterId: 'encounter-id',
    patient: 'patient',
    documentReferenceId: 'document-reference-id',
  },
} as const;

export enum TaskAlertCode {
  abnormalLabResult = 'abnormal-lab-result',
}
export const TaskAlertDisplay = {
  [TaskAlertCode.abnormalLabResult]: 'This result contains an abnormal result',
};

export const FAX_TASK = {
  category: 'inbound-fax',
  system: 'inbound-fax-task',
  code: {
    matchInboundFax: 'match-inbound-fax',
  },
  input: {
    senderFaxNumber: 'sender-fax-number',
    pageCount: 'page-count',
    communicationId: 'communication-id',
    pdfUrl: 'pdf-url',
    receivedDate: 'received-date',
  },
  businessStatus: {
    filed: 'filed',
    deleting: 'deleting',
  },
} as const;

const hasInboundFaxBusinessStatus = (task: FhirTask, code: string): boolean =>
  !!task.businessStatus?.coding?.some((coding) => coding.system === FAX_TASK.system && coding.code === code);

/**
 * True once an inbound fax has been filed into a patient's chart. Filing records this on the Task's
 * `businessStatus` but leaves the Task open: like a manual task, it is completed by a person pressing
 * Complete in the Tasks queue.
 */
export const isInboundFaxFiled = (task: FhirTask): boolean =>
  hasInboundFaxBusinessStatus(task, FAX_TASK.businessStatus.filed);

/** True while a delete has reserved the fax (its PDF may already be gone); filing must refuse it. */
export const isInboundFaxDeleting = (task: FhirTask): boolean =>
  hasInboundFaxBusinessStatus(task, FAX_TASK.businessStatus.deleting);

/**
 * Reads a valueString Task.input from a Task created via the zambdas' `createTask` helper,
 * which encodes the input type as `type.coding[].code`. Security-sensitive values (e.g. the
 * inbound-fax pdf url) must be sourced from the verified Task via this helper — never from
 * client request bodies.
 */
export const getTaskInputValue = (task: FhirTask, inputTypeCode: string): string | undefined =>
  task.input?.find((input) => input.type?.coding?.some((coding) => coding.code === inputTypeCode))?.valueString;

export const RADIOLOGY_TASK = {
  category: 'radiology',
  system: 'radiology-task',
  code: {
    reviewFinalResultTask: 'review-final',
  },
  input: {
    patientName: 'patient-name',
    orderDate: 'order-date',
    appointmentId: 'appointment-id',
    providerName: 'provider-name',
    studyTypeDisplay: 'study-type-display',
    studyTypeCode: 'study-type-code',
  },
} as const;

export const ERX_TASK = {
  category: 'erx',
  system: 'erx-task',
  code: {
    providerNotification: 'provider-notification',
  },
  input: {
    providerName: 'provider-name',
  },
} as const;
